import { authentication } from "@microsoft/teams-js";
import Axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from "axios";
import config from "../../config";
import { useState, useEffect, useContext } from "react";
import { StateContext } from "../store/state-context";
import axios from "axios";
import { apiConstants, appSupportedConstants, errorLoggingConstants } from "../../utility/app-constants";
import { LogError } from "../../utility/app-utilities";

declare type CustomRequestConfig = AxiosRequestConfig & { _retry?: boolean };
let updatedStates: any = "";

/**
 * API Interceptor for attaching the common header token to make API calls
 * @param componentName 
 * @param headersOption 
 * @returns 
 */
export function APIInterceptor(componentName = "", headersOption = {}): [api: AxiosInstance] {
    const [reqInterceptor, setReqInterceptor] = useState<number>();
    const [iaxios] = useState<AxiosInstance>(() => { return Axios.create(); });
    const [state, dispatch] = useContext(StateContext);
    const apiEndpointUrl = config.apiEndpoint;
    updatedStates = state;

    /* Get Auth token from API and store it in local storage with expiration and regenerating once it is expired */
    const GetAuthToken = async () => {
        try {
            const authExpiry = localStorage.getItem(apiConstants.authTokenExpiry)?.toString();
            const currentTime = new Date();
            const expiryTime = new Date(authExpiry!);
            expiryTime.setHours(expiryTime.getHours() - 1);
            if (authExpiry === undefined || expiryTime < currentTime) {
                const authReqHeaders = {
                    [apiConstants.headerContentTypeKey]: apiConstants.headerContentTypeValue,
                    [apiConstants.headerAuthorizationKey]: apiConstants.tokenTypeBasic + config.basicAuthCredentials
                }
                const authReqBody = {
                    [apiConstants.grantTypeKey]: apiConstants.grantTypeValue,
                    [apiConstants.scopeKey]: apiConstants.scopeValue
                }
                const authToken = await axios.post(config.apiEndpoint + apiConstants.authTokenEndpoint, { ...authReqBody }, {
                    headers: authReqHeaders
                });
                localStorage.setItem(apiConstants.authToken, authToken?.data.access_token);
                let today = new Date();
                today.setUTCSeconds(today.getUTCSeconds() + authToken?.data.expires_in);
                localStorage.setItem(apiConstants.authTokenExpiry, today.toString());
                dispatch({ type: "set", state: { authToken: authToken?.data.access_token } });
                dispatch({ type: "set", state: { tokenAPIStatus: appSupportedConstants.apiSucessStatus } });
                dispatch({ type: "set", state: { errorMessage: "" } });
                return authToken?.data.access_token;
            }
            else {
                return localStorage.getItem(apiConstants.authToken);
            }
        }
        catch (error) {
            dispatch({ type: "set", state: { tokenAPIStatus: appSupportedConstants.apifailedStatus } });
            dispatch({ type: "set", state: { errorMessage: error } });
        }
    }

    /**
     * Get Person Session token from API and store it in local storage with expiration and regenerating once it is expired
     * @param authToken 
     * @returns 
     */
    const GetPersonToken = async (authToken: string) => {
        try {
            const sessionExpiry = localStorage.getItem(apiConstants.personTokenExpiry)?.toString();
            const currentTime = new Date();
            const expiryTime = new Date(sessionExpiry!);
            if (sessionExpiry === undefined || expiryTime < currentTime) {
                const personReqHeaders = {
                    [apiConstants.headerAuthorizationKey]: apiConstants.tokenTypeBearer + authToken,
                    [apiConstants.alightRequestHeader]: { [config.requestHeaderKey!]: config.requestHeaderValue }
                }
                const personReqBody = {
                    clientId: config.alightClientId,
                    brokerUserId: config.brokerUserId,
                    subjectId: config.subjectId,
                    subjectType: config.subjectType,
                    testCfg: config.testCfg,
                    testCfgMap: [{ testCfgMapClientIdKey: config.alightClientId, [config.testCfgMapKey!]: config.testCfgMapValue, [config.testCfgMapInstanceIdKey!]: "" }],
                };
                const personToken = await axios.post(apiEndpointUrl + apiConstants.personTokenEndpoint, { ...personReqBody }, {
                    headers: personReqHeaders
                });
                localStorage.setItem(apiConstants.personToken, personToken?.data.alightPersonSessionToken);
                let today = new Date();
                today.setUTCMinutes(today.getUTCMinutes() + 14);
                localStorage.setItem(apiConstants.personTokenExpiry, today.toString());
                dispatch({ type: "set", state: { personToken: personToken?.data.alightPersonSessionToken } });
                dispatch({ type: "set", state: { tokenAPIStatus: appSupportedConstants.apiSucessStatus } });
                dispatch({ type: "set", state: { errorMessage: "" } });
                return personToken?.data.alightPersonSessionToken;
            }
            else {
                return localStorage.getItem(apiConstants.personToken);
            }
        }
        catch (error) {
            dispatch({ type: "set", state: { tokenAPIStatus: appSupportedConstants.apifailedStatus } });
            dispatch({ type: "set", state: { errorMessage: error } });
            const errorObject = {
                component: errorLoggingConstants.apiInterceptorFile,
                method: errorLoggingConstants.apiInterceptor,
                message: error as string,
                operation: errorLoggingConstants.personTokenAPI,
                params: {}
            };
            LogError(errorObject);
        }
    }

    /* Get Teams SSO token */
    const GetTeamsToken = async () => {
        try {
            let token = await authentication.getAuthToken({ resources: [config.appRegURI as string], silent: true });
            return token;
        }
        catch (error) {
            const errorObject = {
                component: errorLoggingConstants.apiInterceptorFile,
                method: errorLoggingConstants.apiInterceptor,
                message: error as string,
                operation: errorLoggingConstants.teamsTokenAPI,
                params: {}
            };
            LogError(errorObject);
        };
    }

    /* add the interceptor, and if there's already one remove it */
    useEffect(() => {
        if (reqInterceptor) {
            iaxios.interceptors.request.eject(reqInterceptor);
        }
        const interceptor = iaxios.interceptors.request.use(async (req: CustomRequestConfig) => {
            let teamsToken: string | undefined;
            let personToken: string | undefined;
            let authToken: string | undefined;

            if (!updatedStates?.personToken) {
                teamsToken = await GetTeamsToken();
                const authToken = await GetAuthToken();
                if (authToken !== undefined) {
                    personToken = await GetPersonToken(authToken);
                }
            }

            if (req.url?.includes(apiConstants.userEndpointKeyword)) {
                req.headers[apiConstants.aadToken] = teamsToken;
            }
            req.headers[apiConstants.personTokenHeaderKey] = personToken;
            req.headers.accept = "*/*";
            req.headers = { ...req.headers, ...headersOption };
            return req;
        });
        setReqInterceptor(interceptor);
    }, [iaxios]);

    /* add the response interceptor with a one retry option */
    useEffect(() => {
        iaxios.interceptors.response.use(res => {
            return res;
        }, async (error: AxiosError) => {
            const originalRequest = error.config as CustomRequestConfig;
            if (error?.response?.status === 401 && !originalRequest._retry) {
                originalRequest._retry = true;
                return iaxios(originalRequest);
            }
            return Promise.reject(error);
        });
    }, [iaxios]);

    return [iaxios];
}